import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faListCheck, faGlobe, faBuilding, faCamera, faPen, faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import TaskListEditor from '../ClientRequests/TaskListEditor';
import EmptyState from '../EmptyState';
import './clientTasks.css';

// A single read-only task row (used in the page's summary lists).
function TaskRow({ task, onOpenPhoto }) {
    const isPhoto = task.property_attachment_id != null || task.photo_url;
    return (
        <li className="ctask-item">
            {isPhoto
                ? (task.photo_url
                    ? <img className="ctask-item__thumb ctask-item__thumb--clickable" src={task.photo_url} alt=""
                        title="View photo" onClick={() => onOpenPhoto && onOpenPhoto(task.photo_url)} />
                    : <span className="ctask-item__thumb ctask-item__thumb--empty"><FontAwesomeIcon icon={faCamera} /></span>)
                : <span className="ctask-item__dot" />}
            <span className="ctask-item__text">{task.instruction}</span>
            {isPhoto && <span className="ctask-chip ctask-chip--photo"><FontAwesomeIcon icon={faCamera} /> Photo each visit</span>}
            {!isPhoto && task.is_report_required && <span className="ctask-chip ctask-chip--report"><FontAwesomeIcon icon={faCamera} /> Photo report</span>}
        </li>
    );
}

function EditAllUnitsModal({ initial, onClose, onSubmitted }) {
    const [tasks, setTasks] = useState(
        (initial || []).map((t) => ({ id: t.id, instruction: t.instruction, is_report_required: !!t.is_report_required }))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setSaving(true);
        setError('');
        // Drop blank rows before sending the desired list.
        const cleaned = tasks
            .map((t) => ({ id: t.id ?? null, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
            .filter((t) => t.instruction);
        try {
            await axios.post('/api/client-requests/requests/client-tasks/edit', { tasks: cleaned });
            onSubmitted();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not submit your task changes. Please try again.');
            setSaving(false);
        }
    };

    return (
        <div className="ctask-modal__backdrop" onClick={onClose}>
            <div className="ctask-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ctask-modal__head">
                    <h3><FontAwesomeIcon icon={faGlobe} /> Tasks for all units</h3>
                    <button className="ctask-modal__x" onClick={onClose} aria-label="Close">&times;</button>
                </div>
                <p className="ctask-modal__intro">
                    These instructions apply to <strong>every</strong> cleaning at all your properties. Changes are
                    reviewed by our team before they take effect. They won't change bookings that are already completed.
                </p>
                <TaskListEditor
                    tasks={tasks}
                    onChange={setTasks}
                    addLabel="Add a task for all units"
                    emptyHint="No all-units tasks yet. Add instructions that apply to every cleaning."
                    disabled={saving}
                />
                {error && <div className="ctask-modal__error">{error}</div>}
                <div className="ctask-modal__actions">
                    <button className="ctask-btn ctask-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="ctask-btn ctask-btn--primary" onClick={submit} disabled={saving}>
                        {saving ? 'Submitting…' : 'Submit for review'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EditPropertyTasksModal({ property, onClose, onSubmitted }) {
    // Text tasks are editable; photo-backed tasks (from setup pictures) show
    // read-only and are managed via the property's setup photos.
    const textTasks = (property.tasks || []).filter((t) => t.property_attachment_id == null);
    const photoTasks = (property.tasks || [])
        .filter((t) => t.property_attachment_id != null)
        .map((t) => ({ id: t.id, instruction: t.instruction, photo_url: t.photo_url }));

    const originalClean = textTasks.map((t) => ({ id: t.id, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }));
    const [tasks, setTasks] = useState(
        textTasks.map((t) => ({ id: t.id, instruction: t.instruction, is_report_required: !!t.is_report_required }))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const label = `${property.unit_number ? `Unit ${String(property.unit_number).trim()} · ` : ''}${(property.building_name || 'Property').trim()}`;
    const cleaned = tasks
        .map((t) => ({ id: t.id ?? null, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
        .filter((t) => t.instruction);
    const changed = JSON.stringify(cleaned) !== JSON.stringify(originalClean);

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            await axios.post(`/api/client-requests/requests/properties/${property.property_id}/edit`, { property_tasks: cleaned });
            onSubmitted();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not submit your task changes. Please try again.');
            setSaving(false);
        }
    };

    return (
        <div className="ctask-modal__backdrop" onClick={onClose}>
            <div className="ctask-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ctask-modal__head">
                    <h3><FontAwesomeIcon icon={faBuilding} /> {label}</h3>
                    <button className="ctask-modal__x" onClick={onClose} aria-label="Close">&times;</button>
                </div>
                <p className="ctask-modal__intro">
                    Instructions the cleaner follows on every visit to this property. Changes are reviewed by our team
                    before they take effect. Photo tasks come from your setup pictures — edit those on the property page.
                </p>
                <TaskListEditor
                    tasks={tasks}
                    onChange={setTasks}
                    photoTasks={photoTasks}
                    addLabel="Add a task for this property"
                    emptyHint="No property tasks yet. Add instructions for every cleaning here."
                    disabled={saving}
                />
                {error && <div className="ctask-modal__error">{error}</div>}
                <div className="ctask-modal__actions">
                    <button className="ctask-btn ctask-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="ctask-btn ctask-btn--primary" onClick={submit} disabled={saving || !changed}>
                        {saving ? 'Submitting…' : 'Submit for review'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ClientTasks() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const [editingProp, setEditingProp] = useState(null);
    const [flash, setFlash] = useState('');
    const [lightbox, setLightbox] = useState(null);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/client-requests/tasks');
            setData(res.data);
            setError('');
        } catch (err) {
            console.error('Error loading tasks:', err);
            setError('Could not load your tasks. Please refresh.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    if (loading) {
        return (
            <div className="app-loading">
                <div className="app-spinner" />
                <div className="app-spinner-text">Loading your tasks…</div>
            </div>
        );
    }
    if (error || !data) {
        return <div className="ctask"><div className="ctask__error">{error || 'No task data.'}</div></div>;
    }

    const {
        client_tasks: clientTasks = [],
        properties = [],
        pending_request: pending,
        pending_property_ids: pendingPropertyIds = [],
    } = data;

    return (
        <div className="ctask">
            <h1 className="ctask__title"><FontAwesomeIcon icon={faListCheck} /> Tasks</h1>
            <p className="ctask__lead">
                Standing instructions your cleaners follow. Changes are reviewed by our team before they take effect.
            </p>

            {flash && <div className="ctask__flash">{flash}</div>}

            {/* All-units tasks */}
            <div className="ctask-card">
                <div className="ctask-card__head">
                    <div className="ctask-card__title"><FontAwesomeIcon icon={faGlobe} /> Tasks for all units</div>
                    {pending
                        ? <span className="ctask-chip ctask-chip--pending"><FontAwesomeIcon icon={faHourglassHalf} /> Pending review</span>
                        : <button className="ctask-btn ctask-btn--edit" onClick={() => setEditing(true)}>
                            <FontAwesomeIcon icon={faPen} /> Edit tasks
                        </button>}
                </div>
                <div className="ctask-card__sub">Applied to every cleaning across all your properties.</div>
                {pending && (
                    <div className="ctask-card__pending">
                        Your changes to these tasks are awaiting review. You can track them under Home → Your Requests.
                    </div>
                )}
                {clientTasks.length === 0
                    ? <EmptyState compact icon={faGlobe} title="No all-units tasks yet"
                        text="Tasks you add here apply to every cleaning across all your properties." />
                    : <ul className="ctask-list">{clientTasks.map((t) => <TaskRow key={`c-${t.id}`} task={t} onOpenPhoto={setLightbox} />)}</ul>}
            </div>

            {/* Per-property tasks */}
            <div className="ctask-section-title"><FontAwesomeIcon icon={faBuilding} /> Property tasks</div>
            {properties.length === 0 ? (
                <EmptyState icon={faBuilding} title="You have no properties yet"
                    text="Add a property first — you'll be able to set up its cleaning tasks here." />
            ) : properties.map((p) => {
                const isPending = pendingPropertyIds.includes(p.property_id);
                const inactive = p.is_active === false;
                const label = `${p.unit_number ? `Unit ${String(p.unit_number).trim()} · ` : ''}${(p.building_name || 'Property').trim()}`;
                return (
                    <div className="ctask-card" key={`p-${p.property_id}`}>
                        <div className="ctask-card__head">
                            <div className="ctask-card__title">
                                {label}
                                {inactive && <span className="ctask-chip ctask-chip--inactive">Inactive</span>}
                            </div>
                            {isPending
                                ? <span className="ctask-chip ctask-chip--pending"><FontAwesomeIcon icon={faHourglassHalf} /> Pending review</span>
                                : !inactive && (
                                    <button className="ctask-btn ctask-btn--edit" onClick={() => setEditingProp(p)}>
                                        <FontAwesomeIcon icon={faPen} /> Edit tasks
                                    </button>
                                )}
                        </div>
                        <div className="ctask-card__sub">Applied to every cleaning at {label}.</div>
                        {isPending && (
                            <div className="ctask-card__pending">
                                A change to this property is awaiting review. You can edit its tasks again once it's resolved.
                            </div>
                        )}
                        {(p.tasks || []).length === 0
                            ? <EmptyState compact icon={faListCheck} title="No tasks for this property yet"
                                text="Add instructions the cleaner should follow on every visit." />
                            : <ul className="ctask-list">{(p.tasks || []).map((t) => <TaskRow key={`pt-${t.id}`} task={t} onOpenPhoto={setLightbox} />)}</ul>}
                    </div>
                );
            })}

            {editing && (
                <EditAllUnitsModal
                    initial={clientTasks}
                    onClose={() => setEditing(false)}
                    onSubmitted={() => {
                        setEditing(false);
                        setFlash('Your task changes were submitted for review.');
                        fetchTasks();
                    }}
                />
            )}

            {editingProp && (
                <EditPropertyTasksModal
                    property={editingProp}
                    onClose={() => setEditingProp(null)}
                    onSubmitted={() => {
                        setEditingProp(null);
                        setFlash('Your property task changes were submitted for review.');
                        fetchTasks();
                    }}
                />
            )}

            {lightbox && (
                <div className="ctask-lightbox" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt="" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </div>
    );
}
